import { Type } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import {
    XYChart,
    LineSeries,
    ValueAxis,
    DateAxis,
    CircleBullet,
    IXYSeriesDataFields,
} from '@amcharts/amcharts4/charts';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';

import { ChartModel } from './chart.model';
import { chartColors } from '../enums/chart-colors.enum';
import { DateAndValueXYChartOptionsModel } from './date-and-value-x-y-chart-options.model';
import { DateAndValueXYChartUnitEnum } from '../enums/date-and-value-x-y-chart-unit.enum';
import { manualChartExport } from '../helpers/manualChartExport.helper';
import { DataPointModel } from './data-point.model';
import { YAxisModel } from './axis.model';
import { SeriesModel } from './series.model';
import { defaultSeriesColorConfig } from '../config/default-series-color.config';
import { ChartEventModel } from './chart-event.model';
import { ChartMetricsEnum, chartMetricsToDateAndValueXYChartUnitMap } from '../../../Common/Reports/enums/chart-metrics.enum';
import { ReportSeriesModel } from '../../Reports/models/report-series.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { ChartSizeHelper } from '../helpers/chart-size.helper';
import { isNumber } from 'underscore';
import { ChartSpecificTypesEnum } from '../../../Common/Reports/enums/chart-specific-types.enum';

const DEFAULT_VALUE_AXIS_KEY = 'default';

export class DateAndValueXYChart extends ChartModel {
    readonly chartType: Type<XYChart> = XYChart;
    readonly singleSeries: boolean = true;
    declare amchart: XYChart;
    declare options: DateAndValueXYChartOptionsModel;
    declare amseries: LineSeries;
    bullets: CircleBullet[] = [];
    defaultValueAxis: ValueAxis;
    valueAxesConfig: YAxisModel[] = [];
    valueAxes: ValueAxis[] = [];
    dateAxis: DateAxis;
    events: ChartEventModel[];
    public groupDataPoints: DataPointModel[] = [];
    public userTimezone?: string;

    protected readonly exportTypes: (keyof am4core.IExportOptions)[] = ['json', 'csv'];

    /**
     * Build XY Chart with x-axis of dates and y-axis with some numeric values
     */
    public initChart(element: HTMLElement, chartType = this.chartType, watchData: boolean = true): void {
        super.initChart(element, chartType);
        let defaultValueAxisConfig: YAxisModel;
        // multi-series-date-and-value-x-y extends this chart and sets singleSeries = false
        if (this.singleSeries) {
            // fallback case for before multiple axes were supported
            defaultValueAxisConfig = {
                key: DEFAULT_VALUE_AXIS_KEY,
                label: this.options.yAxisTitleText ? this.options.yAxisTitleText : '',
                valueLabel: this.options.yValueLabel,
                unit: this.options.unit,
                percentLabel: this.options.percentLabel,
            };
            if ((this.options as DateAndValueXYChartOptionsModel).seriesDefinitions?.length > 1) {
                defaultValueAxisConfig.color = this.styles.colorMap['analysisColor1'];
            }
            this.defaultValueAxis = this.addYAxis(defaultValueAxisConfig);
            this.valueAxesConfig.push(defaultValueAxisConfig);
            this.valueAxes.push(this.defaultValueAxis);
        }

        this.events = this.options.events || [];

        this.amchart.numberFormatter.bigNumberPrefixes = [
            { 'number': 1e+3, 'suffix': 'K' },
            { 'number': 1e+6, 'suffix': 'M' },
            { 'number': 1e+9, 'suffix': 'B' },
            { 'number': 1e+12, 'suffix': 'T' },
            { 'number': 1e+15, 'suffix': 'Q' }
        ];

        // intentionally set smallNumberPrefixes to empty array so that it will not transform small numbers with wired prefix like 'm' or 'mu' etc.
        this.amchart.numberFormatter.smallNumberPrefixes = [];

        // X-Axis contains the time info (hours or days)
        this.dateAxis = this.amchart.xAxes.push(new DateAxis());
        // Disable vertical grid lines
        this.dateAxis.renderer.grid.template.disabled = true;
        // Enable under axis 'ticks'
        this.dateAxis.renderer.minGridDistance = 50;
        this.dateAxis.renderer.minLabelPosition = 0;
        this.dateAxis.renderer.maxLabelPosition = 1;
        this.dateAxis.renderer.ticks.template.disabled = false;
        this.dateAxis.renderer.ticks.template.strokeOpacity = 1;
        this.dateAxis.renderer.ticks.template.stroke = am4core.color(chartColors.agprText);
        this.dateAxis.renderer.ticks.template.strokeWidth = 1;
        this.dateAxis.renderer.ticks.template.length = 8;

        this.dateAxis.groupData = true;
        this.dateAxis.groupCount = 36;
        if (this.options.groupIntervals) {
            this.dateAxis.groupIntervals.setAll(this.options.groupIntervals);
        } else {
            this.dateAxis.groupIntervals.setAll([
                { timeUnit: 'hour', count: 1 },
                { timeUnit: 'day', count: 1 },
                { timeUnit: 'week', count: 1 },
                { timeUnit: 'month', count: 1 }
            ]);
        }
        if (this.options.dateGranularity) {
            this.dateAxis.baseInterval = this.options.dateGranularity;
        }

        // date line charts can optionally have a set of 'events', which are just things
        // with an associated date, name, and color. they produce vertical lines in the chart,
        // which in amcharts are called 'axisRanges', and used to be called 'guides'.
        for (let i = 0; i < this.events.length; i++) {
            const event = this.events[i];
            const color = event.color;
            const chartEvent = this.dateAxis.axisRanges.create();

            // color of the grid line
            chartEvent.grid.stroke = am4core.color(color);
            chartEvent.grid.strokeWidth = 3.5;
            chartEvent.grid.strokeOpacity = 1;

            // setup the tooltip
            const tooltip = new am4core.Tooltip();
            tooltip.getFillFromObject = false;
            tooltip.background.fill = am4core.color(color);
            tooltip.fontSize = 12;

            // setup the bullet (base of the grid line)
            chartEvent.bullet = new am4core.Circle();
            chartEvent.bullet.width = 6;
            chartEvent.bullet.height = 6;
            if (!this.options?.ignoreBullets) {
                chartEvent.bullet.fill = am4core.color(chartColors.agprWhite);
                chartEvent.bullet.stroke = am4core.color(color);
                chartEvent.bullet.strokeWidth = 1;
            } else {
                chartEvent.bullet.strokeWidth = 5;
                chartEvent.bullet.fill = am4core.color(color);
                chartEvent.bullet.strokeOpacity = 0;
            }

            let tooltipText = `<center style="font-size: 0.875rem; padding: 3px 0 5px 0;">${event.name}</center>`;
            if (event.date) {
                chartEvent.date = new Date(event.date.absoluteDate);
                tooltipText += `<center style="font-size: 0.875rem; padding-bottom: 5px;">${chartEvent.date.toDateString()}</center>`;
            }

            if (event.typeLabel) {
                tooltipText += `<center style="font-size: 0.875rem; padding-bottom: 3px;">${event.typeLabel}</center>`;
            }

            chartEvent.grid.tooltipHTML = tooltipText;
            chartEvent.grid.tooltip = tooltip;
            chartEvent.bullet.tooltipHTML = tooltipText;
            chartEvent.bullet.tooltip = tooltip;
        }
        /**
         * @TODO: This code is supposed to hide every x label, but when the zoom is changed labels are added/removed out of order
         * and labels are doubled up/skipped improperly, if we want lots of ticks with few labels we'll have to make this
         * code work
        dateAxis.renderer.labels.template.adapter.add('text',
            (text, target, key) => {
            return dateAxis.renderer.labels.indexOf(target) % 2 ? text : '';
        });
         */

        // multi-series-date-and-value-x-y extends this chart and sets singleSeries = false
        if (this.singleSeries) {
            if ((this.options as DateAndValueXYChartOptionsModel).seriesDefinitions?.length > 0) {
                const numberOfSeries = (this.options as DateAndValueXYChartOptionsModel).seriesDefinitions.length;
                (this.options as DateAndValueXYChartOptionsModel).seriesDefinitions.forEach((series, index) => {
                    if (index !== 0) {
                        const yAxis: YAxisModel = {
                            key: `series index ${index}`,
                            label: '',
                            valueLabel: this.options.yValueLabel,
                            unit: chartMetricsToDateAndValueXYChartUnitMap.get((series.key) as ChartMetricsEnum),
                            percentLabel: this.options.percentLabel,
                            color: this.styles.colorMap[`analysisColor${index + 1}`],
                            position: (numberOfSeries === 4 &&  index === 1)  ? 'left' : 'right'
                        };
                        this.valueAxesConfig.push(yAxis);
                        this.valueAxes.push(this.addYAxis(yAxis, this.defaultValueAxis));
                        this.addSeries(index, this.series[0], yAxis.key, (series.key as ChartMetricsEnum));
                    } else {
                        this.addSeries(index, this.series[0], defaultValueAxisConfig.key, (series.key as ChartMetricsEnum));
                    }

                });
                if (numberOfSeries > 1) {
                    // Set up the legend for each metric
                    this.amchart.legend = new am4charts.Legend();
                    this.amchart.legend.scrollable = true;
                    this.amchart.legend.contentAlign = 'left';
                    this.amchart.legend.valign = 'bottom';
                    this.amchart.legend.fontSize = 12;
                    this.amchart.legend.labels.template.text = '{name}';
                    this.amchart.legend.labels.template.truncate = true;

                    this.amchart.legend.itemContainers.template.maxWidth = this.options?.maxItemWidth || 100;
                    this.amchart.legend.itemContainers.template.paddingLeft = 0;
                    this.amchart.legend.itemContainers.template.paddingTop = 0;
                    this.amchart.paddingBottom = 0;
                }
            } else {
                this.addSeries(0, this.series[0], this.series[0].valueAxisKey);
            }

            const smallChartWidth = ChartSizeHelper.getWidthAtMinCanvas(3, 1);
            const smallChartHeight = ChartSizeHelper.getCellHeight();
            this.amchart.events.on('sizechanged', event => {
                const chart = event.target;
                if (chart.pixelWidth <= smallChartWidth || chart.pixelHeight <= smallChartHeight) {
                    this.amchart.scrollbarX.startGrip.scale = 0.75;
                    this.amchart.scrollbarX.endGrip.scale = 0.75;
                    this.dateAxis.fontSize = 12;
                    this.valueAxes.forEach(axis => axis.fontSize = 12);
                    if (this.header) {
                        this.header.marginBottom = 5;
                    }
                    if (this.amchart.legend) {
                        this.amchart.legend.maxHeight = 32;
                        this.amchart.legend.itemContainers.template.scale = 0.9;
                    }
                } else {
                    this.amchart.scrollbarX.startGrip.scale = 1;
                    this.amchart.scrollbarX.endGrip.scale = 1;
                    this.dateAxis.fontSize = 14;
                    this.valueAxes.forEach(axis => axis.fontSize = 14);
                    if (this.header) {
                        this.header.marginBottom = 10;
                    }
                    if (this.amchart.legend) {
                        this.amchart.legend.maxHeight = 500;
                        this.amchart.legend.itemContainers.template.scale = 1;
                    }
                }
            });
        }

        this.amchart.scrollbarX = new am4core.Scrollbar();
        this.amchart.scrollbarX.dx = -20;
        this.amchart.scrollbarX.dy = -10;
        this.amchart.scrollbarX.exportable = false;

        // Make a cursor
        // this.amchart.cursor = new XYCursor();
        // this.amchart.cursor.maxTooltipDistance = -1;
        // this.amchart.cursor.xAxis = dateAxis;

        // RESPONSIVE
        if (this.options.responsive) {
            this.amchart.responsive.enabled = true;
            this.amchart.svgContainer.autoResize = true;
            this.amchart.svgContainer.htmlElement.style.maxHeight = '500px';
            this.amchart.svgContainer.htmlElement.style.minHeight = '300px';
            this.amchart.chartContainer.marginTop = 25;
            this.amchart.scrollbarX.marginBottom = 25;
            this.amchart.scrollbarX.align = 'center';
            this.dateAxis.renderer.grid.template.disabled = false;
            this.amchart.cursor = new am4charts.XYCursor();

            this.amchart.events.on('sizechanged', ev => {
                const chart = ev.target;
                if (chart.pixelWidth <= 300) {
                    chart.scrollbarX.dx = -5;
                    chart.scrollbarX.scale = 0.9;
                } else {
                    chart.scrollbarX.dx = -25;
                    chart.scrollbarX.scale = 1;
                }
            });

            // Mid to large screen
            this.amchart.responsive.rules.push(
                {
                    id: 'large',
                    relevant: (target) => target.pixelWidth > 768,
                    state: function (target, stateId) {
                        if (target instanceof am4charts.AxisRendererX) {
                            const xAxisState = target.labels.template.states.create(stateId);
                            xAxisState.properties.fontSize = 16;
                            return xAxisState;
                        }

                        if (target instanceof am4charts.AxisRendererY) {
                            const yAxisState = target.labels.template.states.create(stateId);
                            yAxisState.properties.fontSize = 16;
                            return yAxisState;
                        }

                        if (target instanceof am4charts.ValueAxis) {
                            const valueAxisState = target.states.create(stateId);
                            valueAxisState.properties.fontSize = 16;
                            return valueAxisState;
                        }

                        if (target instanceof am4charts.Cursor) {
                            const cursorState = target.states.create(stateId);
                            cursorState.properties.fontSize = 16;
                            return cursorState;
                        }

                        if (target instanceof am4charts.Series) {
                            if (target.tooltip) {
                                const seriesTooltipState = target.tooltip.states.create(stateId);
                                seriesTooltipState.properties.fontSize = 16;
                                return seriesTooltipState;
                            }
                        }
                    }
                }
            );

            // Small screen
            this.amchart.responsive.rules.push(
                {
                    id: 'small',
                    relevant: (target) => {
                        return target.pixelWidth <= 768;
                    },
                    state: function (target, stateId) {
                        if (target instanceof am4charts.AxisRendererX) {
                            const xAxisState = target.labels.template.states.create(stateId);
                            xAxisState.properties.fontSize = 14;
                            return xAxisState;
                        }

                        if (target instanceof am4charts.AxisRendererY) {
                            const yAxisState = target.labels.template.states.create(stateId);
                            yAxisState.properties.fontSize = 14;
                            return yAxisState;
                        }

                        if (target instanceof am4charts.ValueAxis) {
                            const valueAxisState = target.states.create(stateId);
                            valueAxisState.properties.fontSize = 14;
                            return valueAxisState;
                        }

                        if (target instanceof am4charts.Cursor) {
                            const cursorState = target.states.create(stateId);
                            cursorState.properties.fontSize = 14;
                            return cursorState;
                        }

                        if (target instanceof am4charts.Series) {
                            if (target.tooltip) {
                                const seriesTooltipState = target.tooltip.states.create(stateId);
                                seriesTooltipState.properties.fontSize = 14;
                                return seriesTooltipState;
                            }
                        }
                    }
                }
            );
        }

        // For sparkline chart - disable most elements
        if (this.options.isSparkline) {
            this.amchart.padding(-30, 0, 3, 0);
            this.amchart.svgContainer.autoResize = false;
            this.amchart.maskBullets = false;
            this.amchart.scrollbarX.disabled = true;
            this.amchart.scrollbarX.visible = false;
            this.amchart.zoomOutButton.disabled = true;
            this.amchart.focusable = false;

            this.dateAxis.renderer.grid.template.disabled = true;
            this.dateAxis.renderer.labels.template.disabled = true;
            this.dateAxis.renderer.ticks.template.disabled = true;
            this.dateAxis.cursorTooltipEnabled = false;
            this.dateAxis.dy = -3;

            this.valueAxes.forEach(axis => {
                axis.extraMax = 0;
                axis.extraMin = 0;
                axis.renderer.grid.template.disabled = true;
                axis.renderer.baseGrid.disabled = true;
                axis.renderer.labels.template.disabled = true;
                axis.renderer.minGridDistance = 5;
                axis.cursorTooltipEnabled = false;
            });
        }
        if (this.singleSeries) {
            if (this.options.maxYValue && this.defaultValueAxis) {
                this.defaultValueAxis.max = this.options.maxYValue;
            }
        }

        if (this.options.horizontalLines && this.options.horizontalLines.length > 0) {
            this.options.horizontalLines.forEach((lineDef) => {
                this.addAxisLine(this.defaultValueAxis, lineDef, false);
            });
            if (this.singleSeries && !(this.options as DateAndValueXYChartOptionsModel).seriesDefinitions?.length) {
                this.amchart.legend = new am4charts.Legend();
                this.amchart.legend.contentAlign = 'center';
                this.amchart.legend.valign = 'bottom';
                this.amchart.legend.fontSize = 14;
                this.amchart.legend.labels.template.text = '{name}';
                this.amchart.legend.labels.template.truncate = true;
                this.amchart.legend.markers.template.height = 2;

                this.amchart.legend.itemContainers.template.maxWidth = 100;
                this.amchart.legend.itemContainers.template.paddingLeft = 0;
                this.amchart.legend.itemContainers.template.paddingTop = 0;
                this.amchart.legend.itemContainers.template.clickable = false;
                this.amchart.legend.itemContainers.template.focusable = false;
                this.amchart.legend.itemContainers.template.width = 200;
                this.amchart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
                this.amchart.paddingBottom = 20;

                this.amchart.legend.addData((this.options as DateAndValueXYChartOptionsModel).horizontalLines);
            }
        }

        if (isNumber(this.amchart.paddingRight)) {
            (this.amchart.paddingRight += 15);
        }
    }

    protected addYAxis(yAxis: YAxisModel, syncAxis?: ValueAxis): ValueAxis {
        // Y-Axis contains numeric data
        const axis = this.amchart.yAxes.push(new ValueAxis());

        // set y-axis label (whole axis, not individual steps)
        axis.title.text = yAxis.label;


        // Never show values lower than 0. The adapter is what holds it there when every value is
        // zero: amCharts expands a flat range either side of it, which draws a -1 gridline
        axis.min = 0;
        axis.adapter.add('min', minimum => Math.max(0, minimum ?? 0));

        // Make the top on the axis 10% higher than the highest actual value
        axis.extraMax = 0.1;

        // Only show integer values
        axis.maxPrecision = 0;

        if (yAxis.position === 'right') {
            this.amchart.rightAxesContainer.reverseOrder = true;
            axis.renderer.opposite = true;
        }

        if (syncAxis) {
            axis.syncWithAxis = syncAxis;

            // Adds some values to the chart so that it syncs
            if (syncAxis.max % 2 !== 0) {
                axis.extraMax = 0.125;
            }
        }

        if (yAxis.unit === DateAndValueXYChartUnitEnum.CURRENCY) {
            axis.numberFormatter = new am4core.NumberFormatter();
            const reportModel = this.series[0] as ReportSeriesModel;
            if (reportModel.styles?.currencySymbol){
                axis.numberFormatter.numberFormat = `${reportModel.styles.currencySymbol}#,###.#a`;
            }else if (reportModel.styles?.currency){
                axis.numberFormatter.numberFormat = `#,###.#a ${reportModel.styles.currency}`;
            }
        } else if (yAxis.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS) {
            // Using value instead of categories so that the points themselves
            // will retain float precision, it's only the axis that needs to be categorized.
            // Note that the axis categories don't exactly reflect the thresholds (see ProminenceScoreModel)
            axis.extraMax = 0;
            axis.max = 5;
            const promLabels = this.options.prominenceLabels;

            axis.renderer.labels.template.adapter.add('textOutput', function(text, target) {
                return promLabels[+text];
            });
        }  else {
            axis.numberFormatter.numberFormat = '#.#a';
        }

        if (yAxis.color) {
            axis.renderer.line.strokeOpacity = 1;
            axis.renderer.line.strokeWidth = 2;
            axis.renderer.line.stroke = am4core.color(yAxis.color);
        }

        return axis;
    }

    protected getValueAxisByKey(key?: string | number): ValueAxis {
        if (!key) {
            key = DEFAULT_VALUE_AXIS_KEY;
        }
        return this.valueAxes.find((axis, i) => {
            if (this.valueAxesConfig[i].key === key) {
                return true;
            }

            return false;
        }) || this.defaultValueAxis;
    }

    protected addSeries(
        seriesIndex: number,
        series: SeriesModel,
        valueAxisKey: string | number,
        metricType?: ChartMetricsEnum
    ): LineSeries {
        let color = this.styles?.colorMap[series.key]
            || this.styles?.colorMap[`seriesColor${seriesIndex}`]
            || defaultSeriesColorConfig[seriesIndex % defaultSeriesColorConfig.length];
        if (metricType) {
            color = this.styles.colorMap[`analysisColor${seriesIndex + 1}`];
        };
        this.amseries = this.amchart.series.push(new LineSeries());

        if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS || this.options.chartSpecificType === ChartSpecificTypesEnum.FILLED_LINE) {
            this.amseries.fillOpacity = 1;

            const fillModifier = new am4core.LinearGradientModifier();
            fillModifier.opacities = [1, 0];
            fillModifier.offsets = [0, 1];
            fillModifier.gradient.rotation = 90;
            this.amseries.fill = am4core.color(color);
            this.amseries.segments.template.fillModifier = fillModifier;
        }

        const dataFields: IXYSeriesDataFields = this.amseries.dataFields;
        let yValueIdentifier = 'yValue' + (this.singleSeries ? '' : seriesIndex);

        if (metricType) {
            yValueIdentifier = `yValue_${metricType}`;
        };

        this.amseries.adapter.add('groupDataItem', (val) => {
            let mentionCount = 0;
            val.value = 0;
            for (let i = 0; i < val.dataItem.groupDataItems.length; i++) {
                const item = val.dataItem.groupDataItems[i];
                val.value += item.valueY;
                // for prominence over time chart, yValue is average prominence instead of mentions
                // thus we also calculate the sum of mentionCount
                mentionCount += +item.dataContext['mentionCount'];
            }

            // for prominence chart, when grouping data items, it should use average value instead of sum value
            if (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS && val.dataItem.groupDataItems.length > 0) {
                val.value = +val.value / +val.dataItem.groupDataItems.length;
            }

            val.dataItem.dataContext[yValueIdentifier] = val.value;
            val.dataItem.dataContext['mentionCount'] = mentionCount;

            if ((this.options as DateAndValueXYChartOptionsModel).tooltipHTML && val.dataItem.groupDataItems?.length > 1) {
                // when serveral data points are grouped together as one data bullet,
                // we need to reset the date range in view mentions url to include all date range
                const items = val.dataItem.groupDataItems;
                const startDate = new Date((items[0].dataContext as DataPointModel).rawDateFacetFrom);
                const endDate = new Date((items[items.length - 1].dataContext as DataPointModel).rawDateFacetTo);

                const dateRange = new DateQueryModel();
                dateRange.type = DateQueryTypeEnum.ABSOLUTE;
                dateRange.absoluteRange = {
                    start: startDate,
                    end: endDate
                };
                const filters = new MentionSearchCriteriaModel();
                filters.query.dateQuery = DateQueryModel.makeAbsoluteRange(dateRange, this.userTimezone);
                val.dataItem.dataContext['viewMentionUrl' + seriesIndex] = series instanceof ReportSeriesModel ?
                    (<ReportSeriesModel>series).getSocialSeriesUrl(filters) : (<ReportSeriesModel>series).getMentionSeriesUrl(filters);
                val.dataItem.dataContext['dateRangeLabel'] = moment(startDate).format('YYYY/MM/DD') + ' - ' + moment(endDate).format('YYYY/MM/DD');
            }

            if (val.dataItem.groupDataItems?.length > 1) {
                const items = val.dataItem.groupDataItems;
                const first = items[0].dataContext as DataPointModel;
                const last = items[items.length - 1].dataContext as DataPointModel;
                const startDate: string = moment(first.rawDateFacetFrom).format('ddd MMM D YYYY');
                const endDate: string = moment(last.rawDateFacetTo).format('ddd MMM D YYYY');

                const label = `${startDate} - ${endDate}`;
                let groupDataPoint;
                const index = this.groupDataPoints.findIndex(x => x.label === label);
                if (index > -1) {
                    groupDataPoint = this.groupDataPoints[index];
                    groupDataPoint[yValueIdentifier] = val.value;

                } else {
                    groupDataPoint = {
                        label,
                        xValueStartTime: first.xValueStartTime,
                        xValueEndTime: last.xValueEndTime,
                    };
                    groupDataPoint[yValueIdentifier] = val.value;

                    this.groupDataPoints.push(groupDataPoint);
                }
            }

            return val;
        });
        this.amseries.yAxis = this.getValueAxisByKey(valueAxisKey);

        dataFields.valueY = yValueIdentifier;
        dataFields.dateX = 'xValue';

        let formatUnit = this.options.unit;
        if (metricType) {
            formatUnit = chartMetricsToDateAndValueXYChartUnitMap.get(metricType);
        }
        if (this.singleSeries) {
            if (formatUnit === DateAndValueXYChartUnitEnum.NUMBER) {
                this.amseries.tooltipText = `{label} - {${yValueIdentifier}}`;
            } else if (formatUnit === DateAndValueXYChartUnitEnum.CURRENCY) {
                this.amseries.tooltipText = `{label} - {${yValueIdentifier}.formatNumber('#,###.00a')}`;
            } else if  (this.options.unit === DateAndValueXYChartUnitEnum.PROMINENCE_LEVELS) {
                this.amchart.legend = new am4charts.Legend();
                // This longer string will generally require more space than the default of 220 we gave it
                this.amchart.legend.itemContainers.template.maxWidth = 450;
                // If it is Prominence chart, it the series type is ReportSeriesModel and contains prominenceTrendGroup
                this.amchart.legend.labels.template.text =
                    `Prominence of [bold]${(this.series[seriesIndex])['styles']?.prominenceGroupName}[/] in [bold]{name}[/]`;

                const prominenceGroupKeywords = (this.series[seriesIndex])['styles']?.prominenceGroupKeywords?.toString().replace(/,/g, ', ');

                if (prominenceGroupKeywords) {
                    this.amchart.legend.itemContainers.template.tooltip = new am4core.Tooltip();
                    this.amchart.legend.itemContainers.template.tooltipText = prominenceGroupKeywords;
                    this.amchart.legend.itemContainers.template.tooltip.label.background.fill = am4core.color(chartColors.agprBlue);
                    this.amchart.legend.itemContainers.template.tooltip.label.fill = am4core.color(chartColors.agprWhite);
                }
            }
        } else {
            if (formatUnit === DateAndValueXYChartUnitEnum.NUMBER) {
                this.amseries.tooltipText = `{name} - {${yValueIdentifier}}`;

            } else if (formatUnit === DateAndValueXYChartUnitEnum.CURRENCY) {
                this.amseries.tooltipText = `{name} - {${yValueIdentifier}.formatNumber('#,###.00a')}`;
            }
        }

        // hide bullet points when the distance between two bullet points is less than 10px
        if (this.options.responsive) {
            this.amseries.minBulletDistance = 10;
        }

        this.amseries.strokeWidth = 2;
        this.amseries.stroke = am4core.color(color);
        if (this.options.styles?.strokeDasharray) {
            this.amseries.strokeDasharray = this.options.styles.strokeDasharray;
        }

        // Makes the line curved
        if (!this.options.notCurved) {
            this.amseries.tensionX = 0.8;
            this.amseries.tensionY = 0.9;
        }

        const bullet: CircleBullet = this.amseries.bullets.push(new CircleBullet());
        if (!this.options?.ignoreBullets) {
            bullet.circle.strokeWidth = 1;
            bullet.circle.radius = 3;
            bullet.circle.fill = am4core.color('#fff');
            bullet.circle.stroke = am4core.color(color);
        } else {
            bullet.circle.strokeWidth = 5;
            bullet.circle.radius = 1;
            bullet.circle.fill = am4core.color(color);
            bullet.circle.strokeOpacity = 0;
        }

        bullet.propertyFields.id = '' + seriesIndex || ''; // @HACK see ReportsChart - nick
        bullet.propertyFields.disabled = 'bulletDisabled';
        bullet.propertyFields['metric'] = metricType;
        bullet.hoverOnFocus = true;

        bullet.tooltip = new am4core.Tooltip();
        bullet.tooltip.getFillFromObject = false;
        bullet.tooltip.fill = am4core.color(color);
        bullet.tooltip.background.fillOpacity = 1;
        bullet.tooltip.background.fill = am4core.color(color);
        bullet.tooltip.fontSize = 12;


        const bulletHover = bullet.states.create('hover');
        bulletHover.properties.scale = 1.3;

        if (this.options.isSparkline) {
            this.amseries.strokeWidth = 1;
            bullet.circle.radius = 2;
            bullet.tooltip.disabled = true;
            bullet.focusable = false;
        }

        if (seriesIndex !== null) {
            this.amseries.name = series.label;
            if (metricType && this.options.seriesDefinitions.length > 0) {
                this.amseries.name = this.options.seriesDefinitions.find(x => x.key === metricType).label;
            }
        }

        // set tooltip html
        bullet.adapter.add('tooltipHTML', (text, target) => {
            if (target.dataItem.dataContext['label'] !== 'noData' && (this.options as DateAndValueXYChartOptionsModel).tooltipHTML) {
                let res = (this.options as DateAndValueXYChartOptionsModel).tooltipHTML
                    .replace('{viewMentionUrl}',  target.dataItem.dataContext['viewMentionUrl' + seriesIndex])
                    .replace('{color}', color);

                if (target.dataItem.dataContext['dateRangeLabel']) {
                    res = res.replace('{dateX}',  target.dataItem.dataContext['dateRangeLabel']);
                }

                return res;
            }
            return '';
        });
        // set tooltip style
        bullet.tooltip.getFillFromObject = false;
        bullet.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        bullet.tooltip.background.stroke = am4core.color('#ced4da');
        bullet.tooltip.background.cornerRadius = 8;
        bullet.tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        bullet.tooltip.background.filters.clear();
        bullet.tooltip.label.padding(0, 0, 0, 0);
        bullet.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        bullet.tooltip.label.interactionsEnabled = true;
        bullet.tooltip.keepTargetHover = true;
        bullet.tooltip.pointerOrientation = 'horizontal';
        // horizontal pointer orientation isn't smart enough to change the position of tooltip (left or right) based on the bullet point position
        // need to override tooltip's bounding container so the tooltip can always show inside the chart container
        bullet.tooltip.boundingContainer = this.amchart;
        bullet.tooltip.maxWidth = 275;
        bullet.tooltip.label.wrap = true;
        this.bullets.push(bullet);
        return this.amseries;
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);

        if (this.amchart) {
            this.amchart.scrollbarX.visible = false;
            this.amchart.zoomOutButton.visible = false;
        }
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        super.unsetExportStyles();

        if (this.amchart) {
            if ((this.options as DateAndValueXYChartOptionsModel).seriesDefinitions?.length > 1) {
                this.amchart.scrollbarX.visible = false;
                this.amchart.scrollbarX.visible = false;
            } else {
                this.amchart.scrollbarX.visible = true;
                this.amchart.zoomOutButton.visible = true;
            }
        }
    }

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        this.rawData = data;
        if (!this.amchart) {
            return;
        }
        if ((this.options as DateAndValueXYChartOptionsModel).seriesDefinitions?.length > 1) {
            this.amchart.scrollbarX.disabled = true;
            this.amchart.scrollbarX.startGrip.tabindex = -1;
            this.amchart.scrollbarX.endGrip.tabindex = -1;
            this.amchart.scrollbarX.thumb.tabindex = -1;
            this.amchart.zoomOutButton.disabled = true;
        } else {
            this.amchart.scrollbarX.disabled = false;
            this.amchart.scrollbarX.startGrip.tabindex = 0;
            this.amchart.scrollbarX.endGrip.tabindex = 0;
            this.amchart.scrollbarX.thumb.tabindex = 0;
            this.amchart.zoomOutButton.disabled = false;
        }
        if (!data) {
            this.amchart.data = null;
            return;
        }

        if (this.singleSeries) {
            this.amchart.data = JSON.parse(JSON.stringify(data[0]));
        } else {
            this.amchart.data = JSON.parse(JSON.stringify(data));
        }

        if (!data?.includes(null) && this.isContentEmpty()) {
            this.valueAxes[0].disabled = true;
            this.amchart.scrollbarX.disabled = true;
            this.dateAxis.disabled = true;
            this.amseries.disabled = true;
            this.showNoContentMessage();
            if (this.options.isSparkline) {
                this.noContentLabel.y = am4core.percent(45);
            }
        }

        this.updateSeriesStyles();
        this.ready.next(true);
    }

    /**
     * Required but not currently used
     */
    public updateSeriesStyles(): void {}

    /**
     * Exports data using amcharts
     */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {

        let dataFields = {};
        if (this.options.seriesDefinitions?.length){

            this.options.seriesDefinitions.forEach(series => {
                dataFields[`yValue_${series.key}`] = series.label;
            });
        } else {
            dataFields = {
                yValue: this.options.yValueLabel,
            };
        }

        dataFields = {
            label: this.options.label,
            ...dataFields,
            xValueStartTime: this.options.startTimeLabel,
            xValueEndTime: this.options.endTimeLabel
        };

        const exportType = this.exportTypes.find(exportOption => exportOption === type);
        // have to have manually export to properly convert the dates to ignore the minutes
        if (exportType) {
            const useGroupDataPoints = this.options.groupIntervals?.length === 1 && (
                this.options.groupIntervals[0].timeUnit === 'week'
                || this.options.groupIntervals[0].timeUnit === 'month'
                || this.options.groupIntervals[0].timeUnit === 'year'
            );

            if (useGroupDataPoints && this.groupDataPoints?.length) {
                manualChartExport.exportFlatSeriesData(exportType, this.groupDataPoints, dataFields, filename);
            } else {
                manualChartExport.exportFlatSeriesData(exportType, this.amchart.data, dataFields, filename);
            }

            return;
        }

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        return this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return this.amchart.data.length === 0;
    }
}
